/**
 * A `room_role_changed` socket event has two audiences on the client: the room
 * connection, which owns the current user's own role and guest timer, and any
 * member list that happens to be open.
 *
 * The socket lives in the connection hook, so it re-publishes the event on
 * `window` and the member lists listen there. That keeps a component that has
 * no socket of its own — the superadmin console mounts the same list against a
 * different island — from having to reach into the connection layer.
 */

import type { RoomRole, RoomRoleChangedEvent } from 'minka-shared';

export const ROOM_ROLE_CHANGED_EVENT = 'minka:room_role_changed';

/** Re-publish a server role change to everything listening on the window. */
export function publishRoomRoleChange(detail: RoomRoleChangedEvent): void {
  window.dispatchEvent(new CustomEvent<RoomRoleChangedEvent>(ROOM_ROLE_CHANGED_EVENT, { detail }));
}

/**
 * Listen for role changes on one island.
 *
 * @param roomId - only changes on this island call the listener; null listens to none
 * @returns an unsubscribe function for the caller's cleanup
 */
export function onRoomRoleChange(
  roomId: string | null | undefined,
  listener: (detail: RoomRoleChangedEvent) => void
): () => void {
  if (!roomId) {
    return () => undefined;
  }

  const handle = (event: Event) => {
    const detail = (event as CustomEvent<RoomRoleChangedEvent>).detail;
    if (detail?.roomId === roomId) {
      listener(detail);
    }
  };

  window.addEventListener(ROOM_ROLE_CHANGED_EVENT, handle);
  return () => window.removeEventListener(ROOM_ROLE_CHANGED_EVENT, handle);
}

/** The parts of the room connection state a role change touches. */
export interface RoleAwarePlayer {
  userId: string;
  role?: string;
}

/** The parts of the joined island a role change touches. */
export interface RoleAwareRoom {
  currentUserRole?: RoomRole | string;
  expiresAt?: string | null;
}

export interface RoleChangeState<P extends RoleAwarePlayer, R extends RoleAwareRoom> {
  playersData: P[];
  currentRoom: R | null;
}

/**
 * Fold a role change into the live room state.
 *
 * The in-game chrome reads roles off `playersData` — the Admin button is
 * `playersData.find(me).role === 'admin'` — so a promotion that only updated a
 * member list would leave the promoted user without the controls they just
 * gained until they rejoined. The joined island carries the same answer for the
 * current user, and the guest timer reads its expiry from there.
 *
 * A revoked permission (`role: null`) drops the player from the live list; when
 * it is the current user, the caller has already left the island and this is
 * not reached.
 *
 * Pure and total: it returns the same object when nothing changed, so React can
 * skip the render.
 */
export function applyRoomRoleChange<P extends RoleAwarePlayer, R extends RoleAwareRoom>(
  state: RoleChangeState<P, R>,
  event: RoomRoleChangedEvent,
  currentUserId?: string
): RoleChangeState<P, R> {
  const isCurrentUser = !!currentUserId && event.userId === currentUserId;

  const playersData =
    event.role === null
      ? state.playersData.filter(player => player.userId !== event.userId)
      : state.playersData.map(player =>
          player.userId === event.userId ? { ...player, role: event.role as string } : player
        );

  const currentRoom =
    isCurrentUser && state.currentRoom && event.role !== null
      ? { ...state.currentRoom, currentUserRole: event.role, expiresAt: event.expiresAt }
      : state.currentRoom;

  const playersChanged =
    playersData.length !== state.playersData.length ||
    playersData.some((player, index) => player !== state.playersData[index]);

  if (!playersChanged && currentRoom === state.currentRoom) {
    return state;
  }

  return { playersData: playersChanged ? playersData : state.playersData, currentRoom };
}
